import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, FileDown, FileText, Loader2, Trash2, PanelTop, Sun, Umbrella } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api, errMsg, downloadFile, SYSTEM_LABELS } from "./api";
import DesignForm, { DEFAULTS, BWAY_DEFAULTS } from "./DesignForm";
import GridPreview from "./GridPreview";
import ResultsPanel, { StatusBadge } from "./ResultsPanel";
import BomTables from "./BomTables";
import AssemblyDetails from "./AssemblyDetails";
import BwayAssembly from "./BwayAssembly";

const ICONS = { curtain_wall: PanelTop, skylight: Sun, canopy: Umbrella };
const defaultsFor = (type, bway) => (bway ? BWAY_DEFAULTS : DEFAULTS[type]);
const blank = (type, bway) => ({ id: null, name: bway ? "BWAY Slim Pro CW 01" : `${SYSTEM_LABELS[type]} 01`, system_type: type, inputs: { ...defaultsFor(type, bway) } });
const asDraft = (d, bway) => ({ id: d.id, name: d.name, system_type: d.system_type, inputs: { ...defaultsFor(d.system_type, bway), ...d.inputs } });

const DesignList = ({ designs, activeId, onOpen, onNew, bway }) => (
  <div className="space-y-4">
    <div className={`grid gap-2 ${bway ? "grid-cols-1" : "grid-cols-3"}`}>
      {(bway ? ["curtain_wall"] : Object.keys(SYSTEM_LABELS)).map((t) => {
        const Icon = ICONS[t];
        return (
          <button key={t} data-testid={`new-system-${t}`} onClick={() => onNew(t)} className="flex flex-col items-center gap-1.5 rounded-md border border-zinc-800 p-3 text-[11px] text-zinc-400 transition-colors duration-200 hover:border-sky-500/50 hover:text-white">
            <Icon className="h-4 w-4 text-sky-400" />{bway ? "New BWAY Slim Pro curtain wall" : SYSTEM_LABELS[t].replace("Semi-Unitized ", "")}
          </button>
        );
      })}
    </div>
    <div className="space-y-1.5" data-testid="saved-designs-list">
      {designs.length === 0 && <p className="text-sm text-zinc-500">No saved systems yet.</p>}
      {designs.map((d) => (
        <button key={d.id} data-testid={`design-item-${d.id}`} onClick={() => onOpen(d)} className={`flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left transition-colors duration-150 ${d.id === activeId ? "border-sky-500/50 bg-sky-500/10" : "border-zinc-800 hover:border-zinc-600"}`}>
          <div className="min-w-0">
            <div className="truncate text-sm text-white">{d.name}</div>
            <div className="font-mono text-[10px] uppercase text-zinc-500">{d.inputs.profile_source === "bway" ? `BWAY ${d.inputs.bway_series}-${d.inputs.bway_variant} · ${d.inputs.design_code === "adm" ? "ADM" : "IS/AAMA"}` : SYSTEM_LABELS[d.system_type]}</div>
          </div>
          <StatusBadge status={d.results.overall_status} />
        </button>
      ))}
    </div>
  </div>
);

export default function SystemsDesigner({ project, openDesign, brand = "bhoruka" }) {
  const bway = brand === "bway";
  const [designs, setDesigns] = useState([]);
  const [draft, setDraft] = useState(blank("curtain_wall", bway));
  const [result, setResult] = useState(null);
  const [calcErr, setCalcErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [dirty, setDirty] = useState(true);
  const timer = useRef(null);

  const load = () => api.get(`/projects/${project.id}/designs`, { params: { brand: bway ? "bway" : undefined } }).then((r) => setDesigns(r.data));
  useEffect(() => { load(); }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (openDesign) { setDraft(asDraft(openDesign, bway)); setDirty(false); load(); }
  }, [openDesign]); // eslint-disable-line react-hooks/exhaustive-deps

  const fullInputs = (inputs) => ({ basic_wind_speed: project.basic_wind_speed, building_height_m: project.building_height_m, terrain_category: project.terrain_category, ...inputs });

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/calculate", { system_type: draft.system_type, inputs: fullInputs(draft.inputs) });
        setResult(data);
        setCalcErr(null);
      } catch (e) {
        setResult(null);
        setCalcErr(errMsg(e, "Calculation failed"));
      }
    }, 400);
    return () => clearTimeout(timer.current);
  }, [draft.system_type, draft.inputs]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); };

  const save = async () => {
    setBusy("save");
    const body = { name: draft.name, system_type: draft.system_type, inputs: fullInputs(draft.inputs) };
    try {
      const { data } = draft.id ? await api.put(`/designs/${draft.id}`, body) : await api.post(`/projects/${project.id}/designs`, body);
      setDraft({ id: data.id, name: data.name, system_type: data.system_type, inputs: data.inputs });
      setResult(data.results);
      setDirty(false);
      toast.success("System saved");
      load();
      return data;
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const download = async (kind) => {
    const saved = dirty || !draft.id ? await save() : { id: draft.id };
    if (!saved) return;
    setBusy(kind);
    try {
      await downloadFile(`/designs/${saved.id}/${kind}`, `${draft.name.replace(/\W+/g, "_")}_${draft.system_type}.${kind === "calc-report" ? "calc.pdf" : kind}`);
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!draft.id || !window.confirm("Delete this system?")) return;
    try {
      await api.delete(`/designs/${draft.id}`);
      toast.success("System deleted");
      setDraft(blank(draft.system_type, bway));
      load();
    } catch (e) {
      toast.error(errMsg(e, "Delete failed"));
    }
  };

  return (
    <div className="grid gap-6 xl:grid-cols-12">
      <div className="min-w-0 space-y-6 xl:col-span-4">
        <DesignList bway={bway} designs={designs} activeId={draft.id} onOpen={(d) => { setDraft(asDraft(d, bway)); setDirty(false); }} onNew={(t) => { setDraft(blank(t, bway)); setDirty(true); }} />
        <div className="rounded-lg border border-zinc-800 bg-[#121215] p-5">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-amber-400">{bway ? "BWAY Slim Pro curtain wall" : SYSTEM_LABELS[draft.system_type]}</div>
          <Input data-testid="design-name-input" value={draft.name} onChange={(e) => update({ name: e.target.value })} className="mb-5 border-zinc-800 bg-zinc-900 font-[Manrope] font-semibold" />
          <DesignForm type={draft.system_type} inputs={draft.inputs} project={project} brand={brand} onChange={(inputs) => update({ inputs })} />
          <div className="mt-6 flex flex-wrap gap-2">
            <Button data-testid="save-design-btn" onClick={save} disabled={!!busy} className="bg-sky-500 text-white hover:bg-sky-600">
              {busy === "save" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{draft.id ? "Update" : "Save"}
            </Button>
            {draft.id && <Button data-testid="delete-design-btn" variant="outline" onClick={remove} className="border-zinc-700 text-zinc-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></Button>}
          </div>
        </div>
      </div>
      <div className="min-w-0 space-y-5 xl:col-span-8">
        <div className="flex flex-wrap items-center gap-3">
          {result && <span className="flex items-center gap-2 text-sm text-zinc-400">Overall <StatusBadge status={result.overall_status} testid="overall-status" /></span>}
          {result?.design_code && <span className="rounded border border-zinc-800 px-2 py-0.5 font-mono text-[10px] text-zinc-400" data-testid="design-code-badge">{result.design_code}</span>}
          {result && <span className="font-mono text-xs text-zinc-500" data-testid="grid-summary">{result.grid.cols} × {result.grid.rows} modules · {result.grid.module_w_mm} × {result.grid.module_h_mm} mm</span>}
          <div className="ml-auto flex gap-2">
            <Button data-testid="download-dxf-btn" variant="outline" size="sm" disabled={!!busy || !result} onClick={() => download("dxf")} className="border-zinc-700 hover:text-white">
              {busy === "dxf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}DXF drawing
            </Button>
            <Button data-testid="download-pdf-btn" variant="outline" size="sm" disabled={!!busy || !result} onClick={() => download("pdf")} className="border-zinc-700 hover:text-white">
              {busy === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}PDF report
            </Button>
            {draft.system_type === "curtain_wall" && (
              <Button data-testid="download-calc-report-btn" variant="outline" size="sm" disabled={!!busy || !result} onClick={() => download("calc-report")} className="border-amber-500/40 text-amber-300 hover:text-white">
                {busy === "calc-report" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}Calc report
              </Button>
            )}
          </div>
        </div>
        {calcErr && <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300" data-testid="calc-error">{calcErr}</div>}
        {result && (
          <>
            <GridPreview result={result} />
            <Tabs defaultValue="checks">
              <TabsList className="h-auto flex-wrap justify-start border border-zinc-800 bg-[#121215]">
                <TabsTrigger value="checks" data-testid="tab-checks">Engineering</TabsTrigger>
                <TabsTrigger value="bom" data-testid="tab-bom">BOM & Cutting List</TabsTrigger>
                {draft.system_type === "curtain_wall" && <TabsTrigger value="assembly" data-testid="tab-assembly">Assembly Details</TabsTrigger>}
              </TabsList>
              <TabsContent value="checks" className="mt-4"><ResultsPanel result={result} /></TabsContent>
              <TabsContent value="bom" className="mt-4"><BomTables result={result} name={draft.name} /></TabsContent>
              {draft.system_type === "curtain_wall" && <TabsContent value="assembly" className="mt-4">{draft.inputs.profile_source === "bway"
                  ? <BwayAssembly key={`${draft.id}-${result.members[0].profile}-${draft.inputs.bway_variant}-${draft.inputs.bway_series}`} designId={draft.id} result={result} inputs={draft.inputs} />
                  : <AssemblyDetails key={`${draft.id}-${result.members[0].profile}`} designId={draft.id} result={result} inputs={draft.inputs} />}</TabsContent>}
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}
